import cx from "classnames";
import IconButton from "../IconButton/IconButton";
import { Close } from "../Icon/Icon";
import styles from "./Modal.module.scss";
import * as DialogPrimitive from "@radix-ui/react-dialog";
import { Transition } from "react-transition-group";
import { useRef } from "react";
import Loader from "../Loader/Loader";
import cryingMartyImage from "../../images/crying-marty.png";
import martyImage from "../../images/marty.png";

type ModalSize = "xsmall" | "small" | "medium" | "large" | "full-width";

function ModalBody({
  children,
  className,
}: {
  children?: React.ReactNode;
  className?: string;
}) {
  return <div className={cx(styles.body, className)}>{children}</div>;
}

function ModalHeader({
  title,
  subtitle,
  onClose,
  sticky,
  showHeaderImage,
  headerImage = "normal",
}: {
  title?: React.ReactNode;
  subtitle?: React.ReactNode;
  onClose: () => void;
  sticky?: boolean;
  showHeaderImage?: boolean;
  headerImage?: "crying" | "normal";
}) {
  return (
    <>
      <div
        className={cx(styles.header, {
          [styles.stickyHeader]: sticky,
        })}
      >
        <div className={styles.headerText}>
          <DialogPrimitive.Title className={styles.title}>
            {title}
          </DialogPrimitive.Title>
        </div>
        {showHeaderImage && (
          <div className={styles.headerImageContainer}>
            <img
              src={headerImage === "normal" ? martyImage : cryingMartyImage}
              alt=""
              className={styles.headerImage}
            />
          </div>
        )}
        <IconButton
          aria-label="Close"
          showTooltip={false}
          IconComponent={Close}
          iconSize="medium"
          className={styles.close}
          onClick={onClose}
        />
      </div>

      {subtitle ? (
        <DialogPrimitive.Description className={styles.subtitle}>
          {subtitle}
        </DialogPrimitive.Description>
      ) : null}
    </>
  );
}

function Modal({
  isOpen,
  onClose,
  children,
  title,
  subtitle,
  size = "small",
  classNames,
  loading = false,
  hideHeader,
  fullHeight,
  stickyHeader,
  showHeaderImage,
  headerImage,
}: {
  isOpen: boolean;
  onClose: () => void;
  children?: React.ReactNode;
  title?: React.ReactNode;
  subtitle?: React.ReactNode;
  size?: ModalSize;
  classNames?: {
    body?: string;
    overlay?: string;
    content?: string;
    container?: string;
  };
  loading?: boolean;
  hideHeader?: boolean;
  fullHeight?: boolean;
  stickyHeader?: boolean;
  showHeaderImage?: boolean;
  headerImage?: "crying" | "normal";
}) {
  const ref = useRef<HTMLDivElement>(null);

  return (
    <DialogPrimitive.Root
      open={isOpen}
      onOpenChange={(open) => {
        if (!open) {
          onClose();
        }
      }}
    >
      <Transition nodeRef={ref} in={isOpen} timeout={200}>
        {(state) =>
          state === "exited" ? null : (
            <DialogPrimitive.Portal forceMount>
              <div ref={ref}>
                <DialogPrimitive.Overlay
                  forceMount
                  className={cx(styles.overlay, classNames?.overlay, {
                    [styles.overlayClosing]: state === "exiting",
                  })}
                />
                <DialogPrimitive.Content
                  forceMount
                  onWheel={(e) => e.stopPropagation()}
                  className={cx(styles.content, classNames?.content, {
                    [styles.contentClosing]: state === "exiting",
                  })}
                >
                  <div
                    className={cx(
                      styles.container,
                      styles[size],
                      classNames?.container,
                      {
                        [styles.fullHeight]: fullHeight,
                      },
                    )}
                  >
                    {loading ? (
                      <div className={styles.scrollContainer}>
                        <div className={styles.loader}>
                          <Loader />
                        </div>
                      </div>
                    ) : (
                      <>
                        {!hideHeader && (
                          <ModalHeader
                            sticky={stickyHeader}
                            title={title}
                            subtitle={subtitle}
                            onClose={onClose}
                            showHeaderImage={showHeaderImage}
                            headerImage={headerImage}
                          />
                        )}
                        <div
                          style={{
                            overflow: "hidden",
                            display: "flex",
                            flex: "1",
                            borderRadius: "0 0px 12px 12px",
                          }}
                        >
                          <div className={styles.scrollContainer}>
                            <ModalBody className={classNames?.body}>
                              {children}
                            </ModalBody>
                          </div>
                        </div>
                      </>
                    )}
                  </div>
                </DialogPrimitive.Content>
              </div>
            </DialogPrimitive.Portal>
          )
        }
      </Transition>
    </DialogPrimitive.Root>
  );
}

export default Modal;
